import { DateTime, TimeUnit, TimeZone } from 'timezonecomplete';

import type { BlockId, IBlock } from '@/types/block';

import { getTimezoneAbbreviation, getTotalOffset } from '@/helpers/timezone';
import { MILLIS_IN_BLOCK } from '@/config/variables';
import { WeekDays } from '@/store/enums';

export class Block implements IBlock {
  private readonly _blockId: number;
  private readonly _timezoneName: string;

  private readonly _date: DateTime;

  constructor(blockId: number, timezoneName: string) {
    this._blockId = blockId;
    this._timezoneName = timezoneName;

    this._date = new DateTime(this.blockIdToMillis(blockId), TimeZone.utc()).convert(TimeZone.zone(timezoneName));
  }

  static fromBlockId(blockId: number, timezoneName: string): IBlock {
    return new Block(blockId, timezoneName);
  }

  static fromMillis(utcMillis: number, timezoneName: string): IBlock {
    return new Block(Block.getBlockIdByMillis(utcMillis), timezoneName);
  }

  // /**
  //  * @param day - determines which day of the week the Block is created for
  //  * @param startOrEnd - determines if the Block object is created from first or last block of given day
  //  */
  static fromWeekId(
    weekId: number,
    timezoneName: string,
    day: WeekDays = WeekDays.Monday,
    startOrEnd: 'start' | 'end' = 'start',
  ): IBlock {
    const blockId = Block.getBlockIdByWeekId(weekId, timezoneName, day, startOrEnd);

    return new Block(blockId, timezoneName);
  }

  static getBlockIdByWeekId(
    weekId: number,
    timezoneName: string,
    day: WeekDays = WeekDays.Monday,
    startOrEnd: 'start' | 'end' = 'start',
  ): number {
    const timestamp = getTimestampFromWeekId(weekId, timezoneName, day, startOrEnd);
    return Block.getBlockIdByMillis(timestamp);
  }

  static getBlockIdByMillis(utcMillis: number): BlockId {
    return Math.floor(utcMillis / MILLIS_IN_BLOCK);
  }

  private blockIdToMillis(blockId: BlockId): number {
    return blockId * MILLIS_IN_BLOCK;
  }

  /**
   * Supported formats are available on https://github.com/SpiritIT/timezonecomplete/blob/master/doc/LDML.md
   */
  format(dateFormat: string): string {
    return this._date.format(dateFormat);
  }

  getTimezoneAbbreviation(): string {
    return getTimezoneAbbreviation(this._timezoneName, this._date.unixUtcMillis());
  }

  startOfDay(): IBlock {
    const startOfDay = this._date.startOfDay().unixUtcMillis();
    return Block.fromMillis(startOfDay, this._timezoneName);
  }

  toInterface(): IBlock {
    return {
      blockId: this.blockId,
      utcMillis: this.utcMillis,
      timeZoneOffset: this.timeZoneOffset,
      timeZoneLabel: this.timeZoneLabel,
      year: this.year,
      month: this.month,
      day: this.day,
      hours24: this.hours24,
      hours12: this.hours12,
      minutes: this.minutes,
      ampm: this.ampm,
      format: this.format,
      getTimezoneAbbreviation: this.getTimezoneAbbreviation,
      startOfDay: this.startOfDay,
    };
  }

  get blockId(): number {
    return this._blockId;
  }

  get utcMillis(): number {
    return this._date.unixUtcMillis();
  }

  get timeZoneOffset(): number {
    return getTotalOffset(this._timezoneName, this.blockIdToMillis(this._blockId))?.amount() ?? 0;
  }

  get timeZoneLabel(): string {
    return this._timezoneName;
  }

  get year(): number {
    return this._date.year();
  }

  get month(): number {
    return this._date.month();
  }

  get day(): number {
    return this._date.day();
  }

  get hours24(): number {
    return this._date.hour();
  }

  get hours12(): number {
    const hour = this._date.hour() % 12;
    if (hour === 0) return 12;
    return hour;
  }

  get minutes(): number {
    return this._date.minute();
  }

  get ampm(): string {
    return this._date.hour() > 12 ? 'PM' : 'AM';
  }
}

const getTimestampFromWeekId = (
  weekId: number,
  tz: string,
  day: WeekDays = WeekDays.Monday,
  startOrEnd: 'start' | 'end' = 'start',
): number => {
  const weekStart = new DateTime(0, TimeZone.utc()).add(weekId, TimeUnit.Week).convert(TimeZone.zone(tz));
  if (startOrEnd === 'start') {
    return weekStart
      .sub(weekStart.weekDay() - 1, TimeUnit.Day)
      .add(day, TimeUnit.Day)
      .startOfDay()
      .unixUtcMillis();
  }

  // To get end of given day there is added additional day and subtracted 1ms to get the very last millisecond of day
  return (
    weekStart
      .sub(weekStart.weekDay() - 1, TimeUnit.Day)
      .add(day + 1, TimeUnit.Day)
      .startOfDay()
      .unixUtcMillis() - 1
  );
};
